import { useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { STOCK_CAR_SEARCH } from '../../schemas/StockCarSearch';
import { oneGraphClient } from '../../constants/environment';
import {
  addMoreVehicles,
  selectVehiclesOneGraph,
  setPagination,
  setPossibleFilters,
  setTotalCount,
  setVehicleList,
} from '../../redux/vehiclesReducer';
import { selectFilters } from '../../redux/filtersReducer';
import { selectAppConfig } from '../../redux/appConfigReducer';
import useApiFiltersProcessor from '../Filters/useApiFiltersProcessor';
import filterAvailableFilters from '../../utils/filterAvailableFilters';

const useGetStockCarSearch = () => {
  const dispatch = useDispatch();

  const { confirmedFilters } = useSelector(selectFilters);
  const { marketType, isTier3page, dataSource } = useSelector(selectAppConfig);
  const { sortBy, pagination } = useSelector(selectVehiclesOneGraph);

  const { vehicleType, nonAudi } = confirmedFilters;

  const { userLocation, dealersList, vehiclesType, shouldListVehicles, combinedFilters } =
    useApiFiltersProcessor();

  const finilizedCriteria = useMemo(() => {
    if (dealersList)
      return [...combinedFilters, dealersList].filter(
        (toCheck) => Object.keys(toCheck as any).length > 0,
      );
    return [...combinedFilters];
  }, [combinedFilters, dealersList]);

  const sortByPayload = useMemo(() => {
    return {
      id: sortBy.id,
      direction: sortBy.direction,
    };
  }, [sortBy.direction, sortBy.id]);

  const market = marketType === 'USA' ? 'us' : 'ca';

  const queryVariables = useMemo(() => {
    return {
      stockIdentifier: {
        marketIdentifier: {
          brand: 'A',
          country: market,
          language: 'en',
        },
        stockCarsType: vehiclesType,
      },
      searchParameter: {
        geo: userLocation,
        paging: {
          limit: 12,
          offset: 0,
        },
        sort: sortByPayload,
        criteria: finilizedCriteria,
      },
      groupIds: ['renderImagesPNG', 'dealerImages'],
      imageIds: ['sc5c01', 'vtp4x3n3c', 'vtp4x3n5c', '1', '2', '3'],
      // ids: ['body-type', 'exterior-color'],
    };
  }, [finilizedCriteria, market, sortByPayload, userLocation, vehiclesType]);

  const { data, loading, fetchMore, error } = useQuery(STOCK_CAR_SEARCH, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    skip: !shouldListVehicles,
    client: oneGraphClient,
    variables: queryVariables,
  });

  const handleLoadMore = () => {
    fetchMore({
      variables: {
        ...queryVariables,
        searchParameter: {
          ...queryVariables.searchParameter,
          paging: {
            limit: 12,
            offset: pagination.offset + 12,
          },
        },
      },
      updateQuery: (_prev: { stockCarSearch: any }, newData: any) => {
        const vehicles = newData?.fetchMoreResult?.stockCarSearch?.results?.cars;
        const paging = newData?.fetchMoreResult?.stockCarSearch?.results?.paging;
        dispatch(setPagination(paging));
        dispatch(addMoreVehicles(vehicles));
      },
    });
  };

  useEffect(() => {
    if (!data) return;
    const filteredPossibleFilters = filterAvailableFilters(data?.stockCarSearch?.search?.criteria);
    dispatch(setVehicleList(data?.stockCarSearch?.results?.cars));
    dispatch(setPossibleFilters(filteredPossibleFilters));
    dispatch(setTotalCount(data?.stockCarSearch?.resultNumber));
    dispatch(setPagination(data?.stockCarSearch?.results?.paging));
  }, [data, dispatch]);

  const vehicleList = data?.stockCarSearch?.results?.cars;
  const totalCount = data?.stockCarSearch?.resultNumber;

  return { vehicleList, totalCount, loading, handleLoadMore, error };
};

export default useGetStockCarSearch;
